import { captureMcpSignupAttribution, mergeMcpSignupAttribution } from './mcp-signup-attribution';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { mcpAccountLinkConfig as config } from './mcp-embed.config';

/** Connector account linking is separate from embedded-screen authentication.
 * Reuse Mango login/signup, then resume the original MCP OAuth transaction.
 * The request/receipt are opaque; the MCP server verifies identity and the
 * original browser binding. Never put a password in tool arguments or chat.
 */
@Injectable({ providedIn: 'root' })
export class McpAccountLinkService {
  private readonly key = 'mango-connector-request';

  // Capture before ordinary app startup redirects to /mirrors. Fragment removal
  // prevents the continuation value travelling through normal route navigation.
  capture(): boolean {
    if (!this.enabled() || !['/login', '/signup'].includes(location.pathname)) return false;
    const url = new URL(location.href);
    const request = new URLSearchParams(url.hash.slice(1)).get('mcpRequest');
    if (!request) {
      try { return JSON.parse(sessionStorage.getItem(this.key) || 'null')?.expires > Date.now(); }
      catch { sessionStorage.removeItem(this.key); return false; }
    }
    history.replaceState(null, '', url.pathname + url.search);
    sessionStorage.removeItem(this.key);
    if (/^[A-Za-z0-9_-]{43}$/.test(request)) {
      sessionStorage.setItem(this.key, JSON.stringify({ request, expires: Date.now() + 1200000, attribution: captureMcpSignupAttribution(url.searchParams) }));
      return true;
    }
    return false;
  }

  signupConversionCode(existing: unknown): unknown {
    if (!this.enabled()) return existing;
    try {
      const pending = JSON.parse(sessionStorage.getItem(this.key) || 'null');
      if (!pending || pending.expires <= Date.now() || !/^[A-Za-z0-9_-]{43}$/.test(pending.request)) return existing;
      // Revalidate stored labels; repeat capture calls and login-to-signup navigation
      // retain the original pending request rather than relying on URL parameters.
      const attribution = captureMcpSignupAttribution(new URLSearchParams({
        utm_source: pending.attribution?.source || '', utm_medium: pending.attribution?.medium || '',
      }));
      return mergeMcpSignupAttribution(existing, attribution);
    } catch { return existing; }
  }

  private enabled(): boolean {
    return config.enabled && window.parent === window && location.origin === environment.webAppUrl &&
      /^https:\/\/[^/]+$/.test(config.mcpOrigin);
  }

  async resume(session: { authToken?: string; masterSession?: boolean }): Promise<boolean> {
    if (!this.enabled()) return false;
    const raw = sessionStorage.getItem(this.key);
    if (!raw) return false;
    // Consume locally so a stale request cannot link a later login accidentally.
    sessionStorage.removeItem(this.key);
    const pending = JSON.parse(raw);
    if (pending.expires <= Date.now() || !/^[A-Za-z0-9_-]{43}$/.test(pending.request) ||
        !session?.authToken || session.masterSession) throw new Error('Connection expired');
    const response = await fetch(config.mcpOrigin + '/connect/web-session', {
      method: 'POST', credentials: 'omit', redirect: 'error', cache: 'no-store',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ request: pending.request, authToken: session.authToken,
        browserTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone }),
    });
    if (!response.ok) throw new Error('Connection failed');
    const result = await response.json();
    if (!/^[A-Za-z0-9_-]{43}$/.test(result.receipt)) throw new Error('Invalid continuation');
    // Fixed configured destination; the server checks the original browser cookie.
    location.assign(config.mcpOrigin + '/connect/resume?receipt=' + encodeURIComponent(result.receipt));
    return true;
  }
}
